import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table5 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[5] || 'Source and Client');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[5] || 'Source and Client';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Initialize with sample data if localStorage is empty or has no meaningful data
  const getInitialData = () => {
    const saved = localStorage.getItem('table_table5');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.length > 0) {
          const headers = parsed[0];
          
          // Check if Type column exists, if not add it
          const typeIndex = headers.indexOf('Type');
          if (typeIndex === -1) {
            // Type column doesn't exist - add it after Name column
            const nameIndex = headers.indexOf('Name');
            const insertIndex = nameIndex !== -1 ? nameIndex + 1 : 1;
            
            // Add Type header
            headers.splice(insertIndex, 0, 'Type');
            
            // Add empty Type value to all data rows
            for (let i = 1; i < parsed.length; i++) {
              if (Array.isArray(parsed[i])) {
                parsed[i].splice(insertIndex, 0, '');
              }
            }
          }
          
          if (parsed.length > 1) {
            // Check if there's any meaningful data (rows with Name and Code filled)
            const nameIndex = headers.indexOf('Name');
            const codeIndex = headers.indexOf('Code');
            
            if (nameIndex !== -1 && codeIndex !== -1) {
              // Check if any row has both Name and Code filled
              const hasData = parsed.slice(1).some(row => {
                const name = String(row[nameIndex] || '').trim();
                const code = String(row[codeIndex] || '').trim();
                return name && code;
              });
              
              // If there's meaningful data, return it
              if (hasData) {
                return parsed;
              }
            }
          }
          
          // Return data with Type column added
          return parsed;
        }
      } catch (e) {
        console.error('Error loading table5 data:', e);
      }
    }
    
    // Return empty table - don't auto-generate sample data (data should come from MySQL)
    return [['S no', 'Name', 'Type', 'Code', 'Number', 'Notes']];
  };

  const initialData = getInitialData();

  // No currency dropdown column
  const currencyColumns = [];
  // Custom dropdown column: Type column (index 2) with Source/Client/Hybrid options
  const customDropdownColumns = {
    2: ['Source', 'Client', 'Hybrid'] // Type column at index 2
  };
  // Total columns: Sno (0) - removed, shown as stats above title
  const totalColumns = [];
  
  // Calculate count from table data
  // Fetch from MySQL first, fallback to localStorage
  const [tableDataForTotals, setTableDataForTotals] = useState(null);
  
  useEffect(() => {
    const fetchDataForTotals = async () => {
      try {
        // Try MySQL first
        const mysqlData = await tableAPI.getById('table5');
        if (mysqlData && mysqlData.length > 1) {
          setTableDataForTotals(mysqlData);
          // Sync to localStorage
          localStorage.setItem('table_table5', JSON.stringify(mysqlData));
        } else {
          // MySQL empty - clear localStorage and set empty
          localStorage.removeItem('table_table5');
          setTableDataForTotals(null);
        }
      } catch (error) {
        // MySQL failed - try localStorage but validate
        const localData = localStorage.getItem('table_table5');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.length > 1) {
              setTableDataForTotals(parsed);
            } else {
              localStorage.removeItem('table_table5');
              setTableDataForTotals(null);
            }
          } catch (e) {
            localStorage.removeItem('table_table5');
            setTableDataForTotals(null);
          }
        } else {
          setTableDataForTotals(null);
        }
      }
    };
    
    fetchDataForTotals();
    // Refresh every 2 seconds to sync with MySQL
    const interval = setInterval(fetchDataForTotals, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const calculateCounts = () => {
    try {
      // Use fetched data from MySQL
      const parsed = tableDataForTotals;
      if (!parsed || parsed.length < 2) return { sources: 0, clients: 0 };
      
      const headers = parsed[0];
      const typeIndex = headers.indexOf('Type');
      
      let sourcesCount = 0;
      let clientsCount = 0;
      
      parsed.slice(1).forEach(row => {
        if (!row || row.length === 0) return;
        
        // Check if row has at least one non-empty cell (meaningful data)
        const hasData = row.some(cell => cell !== '' && cell !== null && cell !== undefined);
        
        if (hasData && typeIndex !== -1) {
          const type = String(row[typeIndex] || '').trim().toLowerCase();
          // Hybrid counts as both source and client
          if (type === 'source' || type === 'hybrid') {
            sourcesCount++;
          }
          if (type === 'client' || type === 'hybrid') {
            clientsCount++;
          }
        }
      });
      
      return { sources: sourcesCount, clients: clientsCount };
    } catch (e) {
      console.error('Error calculating counts:', e);
      return { sources: 0, clients: 0 };
    }
  };

  const counts = useMemo(() => calculateCounts(), [tableDataForTotals]);

  // Reload count when table data changes (check for actual changes)
  useEffect(() => {
    let lastDataHash = '';
    const interval = setInterval(() => {
      const currentData = localStorage.getItem('table_table5');
      const currentHash = currentData ? JSON.stringify(currentData).slice(0, 1000) : '';
      
      // Only update if data actually changed
      if (currentHash !== lastDataHash) {
        lastDataHash = currentHash;
        setRefreshKey(prev => prev + 1);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[5] = tableName; // Table5 has id 5
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Sources:</span>
                <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                  {counts.sources}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Clients:</span>
                <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                  {counts.clients}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        customDropdownColumns={customDropdownColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table5"
        tableName={tableName}
      />
    </div>
  );
};

export default Table5;

